
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { deriveKeyFromPassword, unwrapKey, exportKeyToSession } from '../utils/crypto';

const SESSION_VAULT_NAME = '_eq_vlt_blob';

interface VaultContextType {
  isVaultLocked: boolean;
  unlockVault: (password: string) => Promise<boolean>;
  lockVault: () => void;
  isUnlocking: boolean;
  unlockError: string;
}

const VaultContext = createContext<VaultContextType | undefined>(undefined);

export const VaultProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [isVaultLocked, setIsVaultLocked] = useState<boolean>(true);
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false);
  const [unlockError, setUnlockError] = useState<string>('');

  useEffect(() => {
    if (isAuthenticated && user?.salt) {
      const existingSessionKey = sessionStorage.getItem(SESSION_VAULT_NAME);
      if (existingSessionKey) {
        setIsVaultLocked(false);
      } else {
        setIsVaultLocked(true);
      }
    } else {
      setIsVaultLocked(false);
    }
  }, [isAuthenticated, user]);

  const unlockVault = useCallback(async (password: string): Promise<boolean> => {
    if (!user?.salt || !user?.encryption_verify) return false;
    
    setIsUnlocking(true);
    setUnlockError('');
    
    try {
      const passwordAccessKey = await deriveKeyFromPassword(password, user.salt);
      const masterDataKey = await unwrapKey(user.encryption_verify, passwordAccessKey);
      
      if (masterDataKey) {
        const exported = await exportKeyToSession(masterDataKey);
        sessionStorage.setItem(SESSION_VAULT_NAME, exported);
        setIsVaultLocked(false);
        return true;
      } else {
        setUnlockError('Chave incorreta.');
        return false;
      }
    } catch (error) {
      setUnlockError('Erro na descriptografia.');
      return false;
    } finally {
      setIsUnlocking(false);
    }
  }, [user]);

  const lockVault = useCallback(() => {
    sessionStorage.removeItem(SESSION_VAULT_NAME);
    setIsVaultLocked(true);
  }, []);

  return (
    <VaultContext.Provider value={{ 
      isVaultLocked, 
      unlockVault, 
      lockVault, 
      isUnlocking, 
      unlockError 
    }}>
      {children}
    </VaultContext.Provider>
  );
};

export const useVault = () => {
  const context = useContext(VaultContext);
  if (context === undefined) {
    throw new Error('useVault must be used within a VaultProvider');
  }
  return context;
};
